import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";

import TrackButtonMobile from "./TrackButtonMobile";
import TrackButtonTablet from "./TrackButtonTablet";
import TrackButtonDesktop from "./TrackButtonDesktop";
import trackVehicle from "./trackVehicle";
import untrackVehicle from "./untrackVehicle";
import { IVehicleDetailsModule } from "../common/analytics/analytics.types";
import analytics from "../common/analytics/analytics";
import { IProductPushable } from "../common/analytics/analytics.types";
import { ErrorContext } from "../../src/context/ErrorContext";
import { checkDegradedServicesStatus } from "../utils/checkDegradedStatus";
import { GET_STATE, SET_TOTAL_TRACKED } from "../redux/slices/appSlice";
import { TRACKED } from "../constants";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import { StoreType } from "../StoreType";

const pushTrackingAnalytics = (
  vehicleAnalytics: IVehicleDetailsModule,
  action: "track" | "untrack"
) => {
  analytics.push<IProductPushable>({
    event: {
      name: "click",
      category: "button",
      label: "vehicle-track",
      action,
    },
    product: {
      vehicle: [vehicleAnalytics],
    },
  });
};

const TrackingWrapper: React.FunctionComponent<{
  saleCode: string;
  vrm: string;
  isVehicleTracked: boolean;
  setIsVehicleTracked: (boolean) => void;
  vehicleAnalytics?: IVehicleDetailsModule;
  inventoryID?: string | null;
}> = ({
  saleCode,
  vrm,
  isVehicleTracked,
  setIsVehicleTracked,
  vehicleAnalytics,
  inventoryID,
}) => {
  const router = useRouter();
  const [isTracked, setIsTracked] = useState(false);
  const errorContext = useContext(ErrorContext);
  const dispatch = useDispatch();

  const {
    app: { isToUseAuthZero },
  }: StoreType = useSelector(GET_STATE);
  const { accessToken } = useAuth(!isToUseAuthZero);

  const { requestOrigin } = router.query;
  const isTrackedPage = requestOrigin === TRACKED;

  useEffect(() => {
    setIsTracked(isVehicleTracked);
  }, [isVehicleTracked]);

  const handleTrackVehicle = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    pushTrackingAnalytics(vehicleAnalytics, "track");

    const result = await trackVehicle(
      vrm,
      inventoryID,
      accessToken,
      isToUseAuthZero
    );
    const degradedServicesResult =
      result.status === 200 &&
      (await checkDegradedServicesStatus(
        accessToken,
        isToUseAuthZero,
        isTrackedPage
      ));

    if (
      result.status === 200 &&
      degradedServicesResult?.degradedServices === true
    ) {
      setIsVehicleTracked(false);
      setIsTracked(false);

      errorContext?.dispatch({
        type: "tracking",
      });
      return;
    }

    if (result.status > 200) {
      if (result.status === 500) {
        errorContext?.dispatch({
          type: "tracking",
        });
      }

      setIsVehicleTracked(false);
      setIsTracked(false);

      return;
    }

    setIsVehicleTracked(true);
    setIsTracked(true);
    dispatch(SET_TOTAL_TRACKED(degradedServicesResult?.totalTracked));
  };

  const handleUntrackVehicle = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    pushTrackingAnalytics(vehicleAnalytics, "untrack");

    const result = await untrackVehicle(
      vrm,
      saleCode,
      inventoryID,
      accessToken,
      isToUseAuthZero
    );
    const degradedServicesResult =
      result.status === 200 &&
      (await checkDegradedServicesStatus(
        accessToken,
        isToUseAuthZero,
        isTrackedPage
      ));

    if (
      result.status === 200 &&
      degradedServicesResult?.degradedServices === true
    ) {
      setIsVehicleTracked(true);
      setIsTracked(true);

      errorContext?.dispatch({
        type: "tracking",
      });

      return;
    }

    if (result.status > 200) {
      if (result.status === 500) {
        errorContext?.dispatch({
          type: "tracking",
        });
      }

      setIsVehicleTracked(true);
      setIsTracked(true);

      return;
    }

    setIsVehicleTracked(false);
    setIsTracked(false);
    dispatch(SET_TOTAL_TRACKED(degradedServicesResult?.totalTracked));
  };

  return (
    <>
      <TrackButtonMobile
        isTracked={isTracked}
        onClick={
          isTracked
            ? (e) => handleUntrackVehicle(e)
            : (e) => handleTrackVehicle(e)
        }
      ></TrackButtonMobile>
      <TrackButtonTablet
        isTracked={isTracked}
        onClick={
          isTracked
            ? (e) => handleUntrackVehicle(e)
            : (e) => handleTrackVehicle(e)
        }
      />
      <TrackButtonDesktop
        isTracked={isTracked}
        onClick={
          isTracked
            ? (e) => handleUntrackVehicle(e)
            : (e) => handleTrackVehicle(e)
        }
      />
    </>
  );
};

export default TrackingWrapper;
